import React from 'react';
import { useSelector } from 'react-redux';
import Chart from 'react-apexcharts';
import { Box, Typography, Grid, CircularProgress, Stack } from '@mui/material';
import CrisisAlertIcon from '@mui/icons-material/CrisisAlert';
import get from 'lodash/get';
import capitalize from 'lodash/capitalize';

import { THREAT_INDEX_COLOR } from 'enum/constants';
import Card from '../components/Card';

const chartOptions = {
  chart: {
    type: 'donut',
    toolbar: {
      show: false,
    },
    zoom: {
      enabled: false,
    },
  },
  legend: {
    show: false,
  },
  colors: [THREAT_INDEX_COLOR.CRITICAL, THREAT_INDEX_COLOR.HIGH, THREAT_INDEX_COLOR.MEDIUM, THREAT_INDEX_COLOR.LOW],
  labels: ['Critical', 'High', 'Medium', 'Low'],
  dataLabels: {
    enabled: false,
  },
  plotOptions: {
    pie: {
      customScale: 0.9,
      donut: {
        size: '75%',
        labels: {
          total: {
            show: true,
            color: '#A3ABB3',
          },
        },
      },
    },
  },
};

const EmptyChart = () => (
  <Box sx={{ position: 'relative', display: 'inline-flex', py: 2, justifyContent: 'center' }}>
    <Box sx={{ position: 'relative' }}>
      {/** background progress bar */}
      <CircularProgress
        value={100}
        variant="determinate"
        size={84}
        thickness={4}
        sx={{ color: (theme) => theme.palette.grey[200] }}
      />
      {/** actual progress bar */}
      <CircularProgress
        value={0}
        variant="determinate"
        size={84}
        thickness={4}
        sx={{ color: '#ccc', position: 'absolute', left: 0 }}
      />
    </Box>
  </Box>
);

const ThreatChart = () => {
  const isLoading = useSelector((state) => get(state.ecosystems.ecosystem.loading, 'threat.event_severity', false));
  const {
    threat: { event_severity: data },
    countData: counters,
  } = useSelector((state) => state.ecosystems.ecosystem);

  const criticalData = (data || []).find(({ Attributes }) => Attributes.event_severity === 'Critical');
  const highData = (data || []).find(({ Attributes }) => Attributes.event_severity === 'High');
  const mediumData = (data || []).find(({ Attributes }) => Attributes.event_severity === 'Medium');
  const lowData = (data || []).find(({ Attributes }) => Attributes.event_severity === 'Low');

  const severityData = {
    LOW: lowData?.Total || 0,
    MEDIUM: mediumData?.Total || 0,
    HIGH: highData?.Total || 0,
    CRITICAL: criticalData?.Total || 0,
  };

  const series = [severityData.CRITICAL, severityData.HIGH, severityData.MEDIUM, severityData.LOW];

  return (
    <Card icon={CrisisAlertIcon} isLoading={isLoading} title="Threats">
      <Grid container>
        <Grid item xs={6} sx={{ borderBottom: '1px solid #EFF2F7' }}>
          <Box sx={{ px: 4, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Typography sx={{ lineHeight: '28px', textAlign: 'center' }}>Threats By Severity</Typography>
            {series.some((severity) => severity !== 0) ? (
              <Box sx={{ width: 100 }}>
                <Chart options={chartOptions} series={series} type="donut" height="120px" />
              </Box>
            ) : (
              <EmptyChart />
            )}
          </Box>
        </Grid>
        <Grid item xs={6} sx={{ border: '1px solid #EFF2F7', borderTop: 'none', borderRight: 'none' }}>
          <Stack>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #EFF2F7' }}
            >
              <Typography>Total Mitigated</Typography>
              <Typography data-testid="text-total_mitigated" sx={{ fontSize: 20, fontWeight: 500, lineHeight: '32px' }}>
                {/** TODO: Use correct value */}
                {counters?.['total.threats']}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, py: 1.5 }}>
              <Typography>Total Threats</Typography>
              <Typography data-testid="text-total_threats" sx={{ fontSize: 20, fontWeight: 500, lineHeight: '32px' }}>
                {counters?.['total.threats']}
              </Typography>
            </Stack>
          </Stack>
        </Grid>
        {Object.keys(severityData).map((severity, index) => (
          <Grid item key={severity} xs={3} sx={{ borderLeft: index === 0 ? 'none' : `1px solid #EFF2F7` }}>
            <Stack sx={{ py: 2 }} columnGap={0.5} alignItems="center">
              <Typography
                sx={{ fontSize: 20, fontWeight: 500, lineHeight: '32px' }}
                data-testid={`threat-severity-${severity.toLowerCase()}`}
              >
                {severityData[severity]}
              </Typography>
              <Stack direction="row" alignItems="center" gap={1}>
                <Box sx={{ height: 12, width: 12, borderRadius: 6, bgcolor: THREAT_INDEX_COLOR[severity] }} />
                <Typography sx={{ fontSize: 14, lineHeight: '20px' }}>{capitalize(severity)}</Typography>
              </Stack>
            </Stack>
          </Grid>
        ))}
      </Grid>
    </Card>
  );
};

export default ThreatChart;
